
// Format price with appropriate decimal places
export function formatPrice(value, currencySymbol = '$') {
  if (value === null || value === undefined) return 'N/A';

  const num = parseFloat(value);
  if (isNaN(num)) return 'N/A';

  // For very small numbers (< $0.01), show more decimal places
  if (num < 0.01) {
    return currencySymbol + num.toFixed(6);
  }

  // For small numbers (< $1), show 4 decimal places
  if (num < 1) {
    return currencySymbol + num.toFixed(4);
  }

  // For regular prices, show 2 decimal places
  if (num < 1000) {
    return currencySymbol + num.toFixed(2);
  }

  // For large numbers, use compact notation
  return currencySymbol + formatNumber(num, 2);
}

// Format market cap and volume with K, M, B, T suffixes
export function formatMarketCap(value, currencySymbol = '$') {
  if (value === null || value === undefined) return 'N/A';

  const num = parseFloat(value);
  if (isNaN(num)) return 'N/A';

  return currencySymbol + formatLargeNumber(num);
}

// Format volume (similar to market cap but without $ prefix for consistency)
export function formatVolume(value, currencySymbol = '$') {
  if (value === null || value === undefined) return 'N/A';

  const num = parseFloat(value);
  if (isNaN(num)) return 'N/A';

  return currencySymbol + formatLargeNumber(num);
}

// Format percentage change with + or - sign
export function formatPercentChange(value) {
  if (value === null || value === undefined) return 'N/A';
  
  const num = parseFloat(value);
  if (isNaN(num)) return 'N/A';

  const sign = num >= 0 ? '+' : '';
  return sign + num.toFixed(2) + '%';
}

// General number formatter with specified decimal places
export function formatNumber(value, decimals = 2) {
  if (value === null || value === undefined) return 'N/A';
  
  const num = parseFloat(value);
  if (isNaN(num)) return 'N/A';

  return num.toFixed(decimals);
}

// Format large numbers with K, M, B, T suffixes
function formatLargeNumber(num) {
  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  if (absNum >= 1e12) {
    return sign + (absNum / 1e12).toFixed(2) + 'T';
  } else if (absNum >= 1e9) {
    return sign + (absNum / 1e9).toFixed(2) + 'B';
  } else if (absNum >= 1e6) {
    return sign + (absNum / 1e6).toFixed(2) + 'M';
  } else if (absNum >= 1e3) {
    return sign + (absNum / 1e3).toFixed(2) + 'K';
  } else {
    return sign + absNum.toFixed(2);
  }
}

// Format RSI value (0-100 range)
export function formatRSI(value) {
  if (value === null || value === undefined) return 'N/A';
  
  const num = parseFloat(value);
  if (isNaN(num)) return 'N/A';

  return num.toFixed(1);
}

// Format Stochastic values (0-100 range)
export function formatStochastic(value) {
  if (value === null || value === undefined) return 'N/A';
  
  const num = parseFloat(value);
  if (isNaN(num)) return 'N/A';

  return num.toFixed(1);
}

// Format MACD histogram value
export function formatMACDHistogram(value) {
  if (value === null || value === undefined) return 'N/A';
  
  const num = parseFloat(value);
  if (isNaN(num)) return 'N/A';

  // Show positive/negative with appropriate precision
  const sign = num >= 0 ? '+' : '';
  return sign + num.toFixed(4);
}

// Format date for display
export function formatDate(timestamp) {
  if (!timestamp) return 'N/A';
  
  try {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch (error) {
    return 'N/A';
  }
}

// Format time for display
export function formatTime(timestamp) {
  if (!timestamp) return 'N/A';
  
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return 'N/A';
  }
}

// Format duration (for cache age, etc.)
export function formatDuration(milliseconds) {
  if (!milliseconds) return '0s';
  
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
}
